import { DocumentReference } from 'firebase/firestore';
import { EntityLabel, ReportingPeriod, ReportingPeriodGroup } from '@esg/esg-global-types';
import moment, { Moment } from 'moment';
import { apollo_client } from '../app/db_util';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { auth } from '../google/firebase';
import {
  GET_REPORTING_PERIODS_BY_GROUP_QUERY,
  GET_REPORTING_PERIODS_QUERY
} from '../graphql/queries/metric_capture/reporting_period';
import {
  CREATE_REPORTING_PERIOD_MUTATOR,
  DELETE_REPORTING_PERIOD_MUTATOR,
  UPDATE_REPORTING_PERIOD_MUTATOR
} from '../graphql/mutations/metric_capture/reporting_period';

interface ReportingPeriodData extends Omit<ReportingPeriod, 'id' | 'start' | 'end'> {
  start: Date;
  end: Date;
}

// Singular and plural label for model entity.
export const reporting_period_label: EntityLabel = {
  one: 'Reporting Period',
  many: 'Reporting Periods'
};

/**
 * Function to query all reporting period documents for current portal company
 * @param {Group} group Group object of current portal company
 * @param {string} company_id id of current portal company to reference firestore documents
 * @param {boolean} locked Request only locked reporting periods if true
 * @returns {Array<ReportPeriod>}
 */
export const getReportingPeriods = async (
  group_id: string,
  company_id: string,
  locked?: boolean
) => {
  try {
    const reporting_periods: Array<ReportingPeriod> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          locked: locked
        },
        query: GET_REPORTING_PERIODS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.reporting_periods;
      });
    return reporting_periods;
  } catch (err) {
    const error = `Error while getting Reporting Periods from Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : '',
      variables: {
        group_id: group_id,
        company_id: company_id,
        locked: locked
      }
    })}`;
    throw new Error(`Error: getReportingPeriods: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to query all reporting period documents for specific reporting group
 * @param {Group} group Group object of current portal company
 * @param {string} company_id id of current portal company to reference firestore documents
 * @param {boolean} locked Request only locked reporting periods if true
 * @returns {Array<ReportPeriod>}
 */
export const getReportingPeriodsByGroup = async (
  group_id: string,
  company_id: string,
  reporting_period_group_id: string
) => {
  try {
    const reporting_periods: Array<ReportingPeriod> = await apollo_client
      .query({
        variables: {
          group_id: group_id,
          company_id: company_id,
          reporting_period_group_id: reporting_period_group_id
        },
        query: GET_REPORTING_PERIODS_BY_GROUP_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.reporting_periods_by_group;
      });
    return reporting_periods;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period.ts failed on an unknown error while calling getReportingPeriodsByGroup.',
      {
        group_id: group_id,
        company_id: company_id,
        reporting_period_group_id: reporting_period_group_id
      },
      tracking_id
    );
  }
};

/**
 * Function to create reporting period with relative data
 * @param {string} group_id ID of Group which Reporting Period is being created for
 * @param {string} company_id ID of Company which Reporting Period is being created for
 * @param {ReportingPeriodData} reporting_period Data of new Reporting Period to add to doc
 * @returns {DocumentReference}
 */
export const createReportingPeriod = async (
  group_id: string,
  company_id: string,
  reporting_period: ReportingPeriodData
) => {
  try {
    const created_reporting_period: DocumentReference = await apollo_client
      .mutate({
        mutation: CREATE_REPORTING_PERIOD_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          reporting_period: reporting_period,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.createReportingPeriod;
      });
    return created_reporting_period;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period.ts failed on an unknown error while calling createReportingPeriod.',
      {
        group_id: group_id,
        company_id: company_id,
        reporting_period: reporting_period
      },
      tracking_id
    );
  }
};

/**
 * Function to update reporting period with relative data and unassign reporting period group if null
 * @param {string} group_id ID of Group to update reporting period for
 * @param {string} company_id ID of Company to update reporting period for
 * @param {ReportingPeriod} updated_reporting_period New Reporting Period data to push into document
 * @returns {void}
 */
export const updateReportingPeriod = async (
  group_id: string,
  company_id: string,
  updated_reporting_period: ReportingPeriod,
  original_reporting_period: ReportingPeriod
) => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_REPORTING_PERIOD_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          updated_reporting_period: updated_reporting_period,
          original_reporting_period: original_reporting_period,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.updateReportingPeriod;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/reporting_period.ts failed on an unknown error while calling updateReportingPeriod.',
      {
        group_id: group_id,
        company_id: company_id,
        updated_reporting_period: updated_reporting_period,
        original_reporting_period: original_reporting_period
      },
      tracking_id
    );
  }
};

/**
 * Function to delete company ReportingPeriod
 * @param {string} group_id ID of Group to delete reporting period for
 * @param {string} company_id ID of Company to delete reporting period for
 * @param {string} reporting_period_id ID of reporting period to delete
 * @returns {void}
 */
export const deleteReportingPeriod = async (
  group_id: string,
  company_id: string,
  reporting_period_id: string,
  reporting_period_name: string
) => {
  if (group_id && company_id && reporting_period_id) {
    await apollo_client
      .mutate({
        mutation: DELETE_REPORTING_PERIOD_MUTATOR,
        variables: {
          group_id: group_id,
          company_id: company_id,
          reporting_period_id: reporting_period_id,
          reporting_period_name: reporting_period_name,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.deleteReportingPeriod;
      });
  }
  return;
};

/**
 * Function to check a given time range for a reporting period against an existing list of reporting periods for overlaps
 * @param {string} id ID of reporting period being checked
 * @param {Moment} start_date Start date of reporting period being checked
 * @param {Moment} end_date End date of the reporting period being checked
 * @param {Array<ReportingPeriod>} existing_reporting_periods List of reporting periods to check against
 * @returns {Array<ReportingPeriod>}
 */
export const checkReportingPeriodOverlap = (
  id: string,
  start_date: Moment,
  end_date: Moment,
  existing_reporting_periods: Array<ReportingPeriod>
) => {
  const overlapped_periods: Array<ReportingPeriod> = existing_reporting_periods.filter(
    (reporting_period: ReportingPeriod) => {
      return (
        start_date.isBefore(reporting_period.end) &&
        end_date.isAfter(reporting_period.start) &&
        id !== reporting_period.id
      );
    }
  );
  return overlapped_periods;
};

/**
 * Function to query reporting periods which are unassigned or conditionally belong to a given reporting period group
 * @param {Array<ReportingPeriod>} reporting_periods Reporting Periods to check availability against
 * @param {ReportingPeriodGroup | null} reporting_period_group Conditionally include reporting periods belonging to given RPG
 * @returns {Array<ReportingPeriod>}
 */
export const getAvailableReportingPeriodsForReportingPeriodGroup = (
  reporting_periods: Array<ReportingPeriod>,
  reporting_period_group: ReportingPeriodGroup | null
) => {
  const available_reporting_periods: Array<ReportingPeriod> = reporting_periods.filter(
    (reporting_period: ReportingPeriod) => {
      const available: boolean =
        reporting_period.reporting_period_group === undefined ||
        reporting_period.reporting_period_group === null ||
        (!!reporting_period_group &&
          reporting_period.reporting_period_group?.id === reporting_period_group.id);
      return available;
    }
  );
  return available_reporting_periods;
};

/**
 * Function to filter reporting periods based on a reporting period group
 * @param {string} reporting_period_group_id ID of Reproting Period Group to filter reporting periods on
 * @param {Array<ReportingPeriod>} reporting_periods Array of reporting periods to filter on
 * @param {boolean | undefined} sort Sorts returned array of reporting periods by ascending start date
 * @returns {Array<ReportingPeriod>} Array of filtered reporting periods
 */
export const reportingPeriodFilterByGroup = (
  reporting_period_group_id: string,
  reporting_periods: Array<ReportingPeriod>,
  sort?: boolean
) => {
  let filtered_reporting_periods: Array<ReportingPeriod> = [];
  if (reporting_period_group_id === 'all') {
    filtered_reporting_periods = reporting_periods;
  } else if (reporting_period_group_id === 'unnassigned') {
    filtered_reporting_periods = reporting_periods.filter(
      (reporting_period) =>
        !reporting_period.reporting_period_group || reporting_period.reporting_period_group === null
    );
  } else {
    filtered_reporting_periods = reporting_periods.filter(
      (reporting_period) =>
        reporting_period.reporting_period_group?.id === reporting_period_group_id
    );
  }
  if (sort) {
    filtered_reporting_periods.sort((a: ReportingPeriod, b: ReportingPeriod) => {
      return moment(a.start).unix() - moment(b.start).unix();
    });
  }
  return filtered_reporting_periods;
};

/**
 * Function to count the days included between the start and end dates of a list of Reporting Periods
 * @param {Array<ReportingPeriod>} reporting_periods Reporting Periods to count days for
 * @returns {number} Number of days
 */
export const getReportingPeriodDays = (reporting_periods: Array<ReportingPeriod>): number => {
  return (
    reporting_periods.reduce((total_days: number, reporting_period: ReportingPeriod) => {
      return total_days + moment(reporting_period.end).diff(moment(reporting_period.start), 'days');
    }, 0) + 1
  );
};

/**
 * Validates that a list of reporting periods does not exceed a given number of consecutive periods.
 * @param {Array<ReportingPeriod>} reporting_periods Array of reporting periods to validate.
 * @param {number} [consecutive_reporting_period_count] Maximum number of allowed consecutive periods. Defaults to 12.
 * @returns {boolean} True if all reporting periods are valid and consecutive; false otherwise.
 */
export const validateConsecutiveReportingPeriods = (
  reporting_periods: Array<ReportingPeriod>,
  consecutive_reporting_period_count = 12
): boolean => {
  if (!Array.isArray(reporting_periods)) return false;

  if (reporting_periods.length > consecutive_reporting_period_count) return false;

  // Sort by start date ascending.
  const reporting_periods_sorted: Array<ReportingPeriod> = [...reporting_periods].sort((a, b) =>
    moment(a.start).diff(moment(b.start))
  );

  // Validate that each period is exactly 1 month apart.
  for (let i = 1; i < reporting_periods_sorted.length; i++) {
    const prev: Moment = moment(reporting_periods_sorted[i - 1].start);
    const current: Moment = moment(reporting_periods_sorted[i].start);

    // Must be exactly 1 month apart.
    if (!current.isSame(prev.clone().add(1, 'month'), 'month')) {
      return false;
    }
  }

  return true;
};
